import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { 
  Mail, Send, Sparkles, Building2, Scissors, 
  CheckCircle, AlertCircle, RefreshCw, Target,
  Users, BarChart3, Calendar, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MarketingPanel = ({ token, settings }) => {
  const [businessType, setBusinessType] = useState('spa');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [recentlySent, setRecentlySent] = useState([]);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSendEmail = async () => {
    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/send-marketing-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_email: email,
          business_type: businessType
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Marketing email sent successfully to ${email}`);
        setRecentlySent(prev => [{
          email,
          businessType,
          sentAt: new Date().toLocaleTimeString()
        }, ...prev.slice(0, 4)]);
        setEmail('');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending marketing email:', error);
      toast.error('Failed to send marketing email');
    } finally {
      setSending(false);
    }
  };

  const trialDays = settings?.trial_days || 7;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Target className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              Marketing Outreach
              <Badge className="bg-emerald-500/20 text-emerald-400">LIVE MODE</Badge>
            </h2>
            <p className="text-slate-400 mt-1">
              Send professional marketing emails to potential spa & salon chain owners across India
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Send Email Card */}
        <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-400" />
              Send Marketing Email
            </CardTitle>
            <CardDescription className="text-slate-400">
              Reach out to potential customers with a professionally crafted email
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Business Type Selection */}
            <div className="space-y-3">
              <Label className="text-white font-medium">Select Business Type</Label>
              <RadioGroup 
                value={businessType} 
                onValueChange={setBusinessType}
                className="flex gap-4"
              >
                <div 
                  className={`flex-1 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    businessType === 'spa' 
                      ? 'border-purple-500 bg-purple-500/10' 
                      : 'border-slate-600 bg-slate-900/50 hover:border-slate-500'
                  }`}
                  onClick={() => setBusinessType('spa')}
                >
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value="spa" id="spa" />
                    <div className="flex items-center gap-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        businessType === 'spa' ? 'bg-purple-500' : 'bg-slate-700'
                      }`}>
                        <Sparkles className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <Label htmlFor="spa" className="text-white font-medium cursor-pointer">Spa</Label>
                        <p className="text-xs text-slate-400">Wellness & Spa Chains</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div 
                  className={`flex-1 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    businessType === 'salon' 
                      ? 'border-pink-500 bg-pink-500/10' 
                      : 'border-slate-600 bg-slate-900/50 hover:border-slate-500'
                  }`}
                  onClick={() => setBusinessType('salon')}
                >
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value="salon" id="salon" />
                    <div className="flex items-center gap-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        businessType === 'salon' ? 'bg-pink-500' : 'bg-slate-700'
                      }`}>
                        <Scissors className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <Label htmlFor="salon" className="text-white font-medium cursor-pointer">Salon</Label>
                        <p className="text-xs text-slate-400">Beauty & Salon Chains</p>
                      </div>
                    </div>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Email Input */}
            <div className="space-y-2">
              <Label htmlFor="recipient-email" className="text-white font-medium">
                Recipient Email Address
              </Label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="recipient-email"
                    type="email"
                    placeholder="Enter email address of potential customer..."
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-slate-900/50 border-slate-600 text-white h-12"
                    onKeyPress={(e) => e.key === 'Enter' && handleSendEmail()}
                  />
                </div>
                <Button
                  onClick={handleSendEmail}
                  disabled={sending || !email.trim()}
                  className="h-12 px-6 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                >
                  {sending ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Marketing Email
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Email will be sent from noreply@wellnesstechnologies.com
              </p>
            </div>

            {/* Email Preview Info */}
            <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700">
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-purple-400" />
                Email Content Preview
              </h4>
              <div className="space-y-2 text-sm text-slate-400">
                <p>The {businessType === 'spa' ? 'Spa' : 'Salon'} marketing email includes:</p>
                <ul className="grid grid-cols-2 gap-2 mt-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                    Platform introduction
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                    Therapist management features
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                    Property partner visibility
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                    Revenue sharing & reconciliation
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                    {trialDays}-day free trial mention
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                    Smart feedback insights
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                    Contact information
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                    Call to action
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats & Recent Activity */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-purple-400" />
                Platform Highlights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" />
                  <span className="text-sm text-slate-400">Free Trial</span>
                </div>
                <Badge className="bg-blue-500/20 text-blue-400">{trialDays} Days</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-slate-400">Multi-Location</span>
                </div>
                <Badge className="bg-emerald-500/20 text-emerald-400">Supported</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-slate-400">Auto Reconciliation</span>
                </div>
                <Badge className="bg-purple-500/20 text-purple-400">Included</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Recently Sent */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Send className="w-4 h-4 text-pink-400" />
                Recently Sent
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentlySent.length === 0 ? (
                <div className="text-center py-6 text-slate-500">
                  <Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No emails sent yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentlySent.map((item, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-2 bg-slate-900/50 rounded-lg text-sm"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <CheckCircle className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                        <span className="text-slate-300 truncate">{item.email}</span>
                      </div>
                      <Badge className={`text-xs flex-shrink-0 ${
                        item.businessType === 'spa' 
                          ? 'bg-purple-500/20 text-purple-400' 
                          : 'bg-pink-500/20 text-pink-400'
                      }`}>
                        {item.businessType}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MarketingPanel;
